'use client';
import React, { useEffect } from 'react';
import tailwindConfig from 'tailwind.config';

import { useOnResize } from '@/decodo/components/atoms/Tabs/tabs.utils';

import { MenuItem, MenuItemProps } from './MenuItem';

export type MenuListProps = {
  items: MenuItemProps[];
  onLinkClick?: (
    params: { title: string } | { url: string; keepMenuOpen?: boolean }
  ) => void;
  suffixItem?: MenuItemProps;
};

const mobileHeaderBreakpoint = parseInt(tailwindConfig.theme.screens.headerLg);

export function MenuList({ items, suffixItem, onLinkClick }: MenuListProps) {
  const ref = React.useRef<HTMLUListElement>(null);

  function adjustWidthForWrap() {
    if (window.innerWidth <= mobileHeaderBreakpoint) return;
    if (!ref.current) return;

    const menuList = ref.current;

    // Map<offsetLeft, offsetWidth>
    const columnsWidthMap = new Map<number, number>();

    Array.from(menuList.children).forEach((menuListItem: Element) => {
      if (!(menuListItem instanceof HTMLElement)) return;
      const { offsetWidth: width, offsetLeft } = menuListItem;
      columnsWidthMap.set(offsetLeft, width);
    });

    let menuListWidth = 0;

    for (const width of Array.from(columnsWidthMap.values())) {
      menuListWidth += width;
    }

    const columnsCount = columnsWidthMap.size;
    const columnGap = parseInt(
      window.getComputedStyle(menuList).getPropertyValue('column-gap')
    );
    menuListWidth += (columnsCount - 1) * columnGap;

    // Sometimes, the items seem to render 1px shorter
    // than they should be causing an overflow effect on text
    menuListWidth += 1;

    if (columnsWidthMap.size === 1) {
      // prevent the bar appearing on items that overflow just a few pixels in a single column setup
      menuList.style.removeProperty('width');
    } else {
      menuList.style.setProperty('width', `${menuListWidth}px`);
    }
  }

  useEffect(() => {
    adjustWidthForWrap();
  }, []);

  useOnResize(adjustWidthForWrap);

  return (
    <ul
      ref={ref}
      className='flex size-full h-full max-h-[calc(100vh-160px)] flex-col flex-wrap gap-x-10 gap-y-2 group-[[data-type="extra"]]/menu-group:gap-0 '
    >
      {items.map((item, index) => (
        <li
          className=' group-[[data-type="extra"]]/menu-group:headerLg:max-w-full headerLg:w-auto w-full origin-left'
          key={item.title + item.url}
        >
          <MenuItem {...item} onLinkClick={onLinkClick} />
        </li>
      ))}
      {suffixItem && (
        <li
          className=' group-[[data-type="extra"]]/menu-group:headerLg:max-w-full group/menu-list-item w-auto origin-left'
          key={suffixItem.title + suffixItem.url}
          data-type='suffix'
        >
          <MenuItem {...suffixItem} />
        </li>
      )}
    </ul>
  );
}
